import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

const AuthContext = createContext(null)

const API_URL = import.meta.env.VITE_API_URL || ''

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(() => localStorage.getItem('mycookbook_token'))

  const validate = useCallback(async (jwt) => {
    if (!jwt) { setLoading(false); return }
    try {
      const res = await fetch(`${API_URL}/auth/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      })
      if (!res.ok) throw new Error('Invalid token')
      const data = await res.json()
      setUser(data.user)
      setToken(jwt)
      localStorage.setItem('mycookbook_token', jwt)
    } catch {
      setUser(null)
      setToken(null)
      localStorage.removeItem('mycookbook_token')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    validate(token)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = useCallback((jwt) => {
    localStorage.setItem('mycookbook_token', jwt)
    setToken(jwt)
    validate(jwt)
  }, [validate])

  const signOut = useCallback(() => {
    setUser(null)
    setToken(null)
    localStorage.removeItem('mycookbook_token')
  }, [])

  const authFetch = useCallback(async (url, options = {}) => {
    const headers = { ...options.headers, Authorization: `Bearer ${token}` }
    const res = await fetch(`${API_URL}${url}`, { ...options, headers })
    if (res.status === 401) { signOut(); throw new Error('Unauthorized') }
    return res
  }, [token, signOut])

  const isSubscriber = user?.tier === 'sub'

  return (
    <AuthContext.Provider value={{ user, loading, token, signIn, signOut, authFetch, isSubscriber }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside AuthProvider')
  return ctx
}
